import {
  ExpandablePanel,
  SectionEditor,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import React from "react";
import { Form } from "react-final-form";

export function BiometricIdentifiersPolicyEditor({
  audience,
  header,
  content,
  placeholder,
  onContentChange,
  saveStatus,
}: {
  audience: "consumer" | "employee";
  header: string;
  content: string;
  placeholder: string;
  onContentChange: (content: string) => void;
  saveStatus?: React.ReactNode;
}) {
  const deletionGuidance = (
    <>
      Delete biometric identifiers at the earliest of:
      <ul className="mt-1 list-disc pl-5">
        <li>
          The date upon which the initial purpose for collecting the biometric identifier has been
          satisfied;
        </li>
        <li>24 months after the consumer last interacted with the controller; or</li>
        <li>
          Within 45 days of determining, in the course of a review conducted at least annually, that
          the biometric identifier is no longer necessary, adequate, or relevant to the processing
          purpose identified above.
        </li>
      </ul>
    </>
  );

  return (
    <div style={{ maxWidth: "764px" }}>
      <div className="tw-boundary">
        <ExpandablePanel
          title={
            audience === "employee"
              ? "Your Colorado notice for employees and contractors must include:"
              : "Here are the requirements for what you must include in the Biometric Identifiers Policy:"
          }
          titleFontSize="base"
          widthClass="w-full"
        >
          <div className="mt-6">
            <VerticalProgressBarCirclesWithText
              steps={[
                {
                  name: "State that a biometric identifier is being collected",
                  status: "complete" as const,
                },
                {
                  name: "Describe the biometric identifier and the specific purpose for which it is collected",
                  status: "complete" as const,
                },
                {
                  name: "Say whether it will be disclosed to a processor (e.g., a vendor) and, if so, the specific purpose for doing so",
                  status: "complete" as const,
                },
                {
                  name: "Disclose the length of time that it will be retained",
                  status: "complete" as const,
                },
                {
                  name: "Include guidelines for deleting biometric identifiers",
                  description: deletionGuidance,
                  status: "complete" as const,
                },
                ...(audience === "employee"
                  ? [
                      {
                        name: "Describe generally the internal protocol for responding to a data security incident involving biometric identifiers, including the process for notifying employees in the event of a data breach that may have compromised their biometric identifiers",
                        description: "You do not need to include specific operational details.",
                        status: "complete" as const,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </ExpandablePanel>
      </div>

      <div style={{ marginTop: "48px" }}>
        <Form
          onSubmit={() => {}}
          render={() => (
            <SectionEditor
              hideNavigationCheckbox
              hideSideControls
              addToNavigation={false}
              header={header}
              headerDisabled
              content={content}
              locked={false}
              placeholder={placeholder}
              onPositionUp={() => {}}
              onPositionDown={() => {}}
              moveDisabled
              moveUpDisabled
              moveDownDisabled
              deleteDisabled
              onShowInNavigationCheckboxToggle={() => {}}
              onDelete={() => {}}
              onInputChange={({ content: newContent }) => {
                if (newContent !== undefined) {
                  onContentChange(newContent);
                }
              }}
              onUnlock={() => {}}
              enablePrivacyCenterToolbar
            />
          )}
        />
        {saveStatus && (
          <div className="tw-boundary">
            <div className="mt-4 flex items-center justify-end whitespace-nowrap text-neutral-600 font-normal">
              {saveStatus}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
